import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import BurnUp from '../../../components/abnahme/statistik/BurnUp';
import ReleaseTimeline from '../../../components/abnahme/statistik/ReleaseTimeline';
import {
  ACHSE,
  ChartKarte,
  EINZELTON,
  FLAECHE,
  KAT_1,
  KAT_2,
  Kachel,
  Legende,
  RASTER,
  STAPEL_REIHENFOLGE,
  STATUS_FARBE,
  VizTooltip,
  prozent,
  statusLegende,
  tagKurz,
  tagLang,
} from '../../../components/abnahme/statistik/viz';
import { STORY_STATUS_LABEL, STORY_STATUS_ORDER, abnahmeApi, type StatistikDto } from '../../../lib/api/abnahme';

const tage = (wert: number | null): string =>
  wert === null ? '—' : `${wert.toLocaleString('de-DE', { maximumFractionDigits: 1 })} Tage`;

const HEUTE = () => new Date().toISOString().slice(0, 10);

/**
 * Interne Statistik der Abnahme (docs/abnahme-plan.md, Abschnitt 11): Fortschritt,
 * Tickets, Durchlaufzeiten, Testläufe und die Release-Zeitleiste.
 */
export default function StatistikPage() {
  const [daten, setDaten] = useState<StatistikDto | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    abnahmeApi
      .statistik()
      .then(setDaten)
      .catch((e: unknown) => setFehler(e instanceof Error ? e.message : 'Die Statistik ließ sich nicht laden.'));
  }, []);

  if (fehler) {
    return <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">{fehler}</div>;
  }
  if (!daten) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Statistik wird geladen …
      </div>
    );
  }

  const { aktuell } = daten;
  const gesamt = aktuell.stories.gesamt;
  const abgenommen = aktuell.stories.status.abgenommen ?? 0;
  const heute = HEUTE();

  // Epics nach Fortschritt sortiert — wer am weitesten ist, steht oben.
  const epics = [...aktuell.epics]
    .filter((e) => e.gesamt > 0)
    .sort((a, b) => (b.status.abgenommen ?? 0) / b.gesamt - (a.status.abgenommen ?? 0) / a.gesamt);
  const epicDaten = epics.map((e) => ({ name: e.key, titel: e.title, ...e.status }));
  const abdeckungDaten = [...aktuell.epics]
    .filter((e) => e.abdeckung !== null)
    .sort((a, b) => (b.abdeckung ?? 0) - (a.abdeckung ?? 0))
    .map((e) => ({ name: e.key, titel: e.title, abdeckung: e.abdeckung }));

  const ticketVerlauf = daten.snapshots
    .flatMap((s) => (s.tickets ? [{ tag: s.tag, offen: s.tickets.offen, blocker: s.tickets.blocker }] : []));
  if (!ticketVerlauf.some((p) => p.tag === heute)) {
    ticketVerlauf.push({ tag: heute, offen: aktuell.tickets.offen, blocker: aktuell.tickets.blocker });
  }

  const ci = daten.laeufe.map((l, i) => ({ ...l, nr: i + 1 }));
  const ciDaten = ci.map((l) => ({
    nr: l.nr,
    am: l.am,
    [l.art]: l.fehlgeschlagen,
  }));

  return (
    <div className="viz-root mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold leading-7">Statistik</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Wie weit die Abnahme ist, wie schnell Tickets durchlaufen und was wann ausgerollt wurde.
          </p>
        </div>
        <Link to="/abnahme/report" className="text-sm underline">
          Zum Report für Auftraggeber
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <Kachel
          titel="Abgenommen"
          wert={prozent(gesamt ? Math.round((1000 * abgenommen) / gesamt) / 10 : null)}
          zusatz={`${abgenommen} von ${gesamt} Stories`}
        />
        <Kachel
          titel="Bereit zur Abnahme"
          wert={aktuell.stories.status.bereit_zur_abnahme ?? 0}
          zusatz="warten auf Freigabe"
        />
        <Kachel
          titel="Kriterien geprüft"
          wert={prozent(aktuell.abdeckung?.prozent)}
          zusatz={aktuell.abdeckung ? `${aktuell.abdeckung.abgedeckt} von ${aktuell.abdeckung.kriterien}` : undefined}
        />
        <Kachel
          titel="Offene Tickets"
          wert={aktuell.tickets.offen}
          zusatz={`davon ${aktuell.tickets.blocker} Blocker`}
          ton={aktuell.tickets.blocker > 0 ? 'warnung' : 'normal'}
        />
        <Kachel titel="Im Nachtest" wert={aktuell.tickets.nachtest} zusatz={`${aktuell.tickets.behoben} behoben`} />
        <Kachel
          titel="Zurückgestellt"
          wert={aktuell.tickets.backlog ?? 0}
          zusatz="zählt in keiner Dauer"
        />
        <Kachel
          titel="Regressionen"
          wert={aktuell.regressionen.length}
          zusatz={aktuell.regressionen.slice(0, 3).join(', ') || 'keine'}
          ton={aktuell.regressionen.length > 0 ? 'warnung' : 'normal'}
        />
      </div>

      <BurnUp snapshots={daten.snapshots} heute={{ tag: heute, stories: aktuell.stories }} />

      <ReleaseTimeline releases={daten.releases} />

      <div className="grid gap-6 lg:grid-cols-2">
        <ChartKarte
          titel="Status je Epic"
          beschreibung="Sortiert nach Anteil abgenommener Stories."
          legende={<Legende eintraege={statusLegende()} />}
          tabelle={{
            spalten: ['Epic', ...STORY_STATUS_ORDER.map((s) => STORY_STATUS_LABEL[s]), 'Gesamt'],
            zeilen: epics.map((e) => [`${e.key} · ${e.title}`, ...STORY_STATUS_ORDER.map((s) => e.status[s] ?? 0), e.gesamt]),
          }}
        >
          <div style={{ height: Math.max(160, epicDaten.length * 26 + 40) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={epicDaten} layout="vertical" margin={{ top: 0, right: 8, bottom: 0, left: 0 }} barSize={14}>
                <CartesianGrid {...RASTER} vertical horizontal={false} />
                <XAxis type="number" allowDecimals={false} {...ACHSE} />
                <YAxis type="category" dataKey="name" width={64} {...ACHSE} axisLine={false} />
                <Tooltip
                  cursor={{ fill: 'var(--viz-raster)', opacity: 0.4 }}
                  content={
                    <VizTooltip
                      labelFormat={(l) => {
                        const epic = epicDaten.find((e) => e.name === l);
                        return epic ? `${epic.name} · ${epic.titel}` : String(l);
                      }}
                    />
                  }
                />
                {STAPEL_REIHENFOLGE.map((status) => (
                  <Bar
                    key={status}
                    dataKey={status}
                    name={STORY_STATUS_LABEL[status]}
                    stackId="e"
                    fill={STATUS_FARBE[status]}
                    stroke={FLAECHE}
                    strokeWidth={2}
                    isAnimationActive={false}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartKarte>

        <ChartKarte
          titel="Testabdeckung je Epic"
          beschreibung="Anteil der Akzeptanzkriterien, die ein Test belegt."
          tabelle={{
            spalten: ['Epic', 'Abdeckung'],
            zeilen: abdeckungDaten.map((e) => [`${e.name} · ${e.titel}`, prozent(e.abdeckung)]),
          }}
        >
          <div style={{ height: Math.max(160, abdeckungDaten.length * 26 + 40) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={abdeckungDaten} layout="vertical" margin={{ top: 0, right: 8, bottom: 0, left: 0 }} barSize={14}>
                <CartesianGrid {...RASTER} vertical horizontal={false} />
                <XAxis type="number" domain={[0, 100]} unit=" %" {...ACHSE} />
                <YAxis type="category" dataKey="name" width={64} {...ACHSE} axisLine={false} />
                <Tooltip
                  cursor={{ fill: 'var(--viz-raster)', opacity: 0.4 }}
                  content={
                    <VizTooltip
                      wertFormat={(w) => prozent(Number(w))}
                      labelFormat={(l) => {
                        const epic = abdeckungDaten.find((e) => e.name === l);
                        return epic ? `${epic.name} · ${epic.titel}` : String(l);
                      }}
                    />
                  }
                />
                <Bar dataKey="abdeckung" name="Abdeckung" fill={EINZELTON} radius={[0, 4, 4, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartKarte>

        <ChartKarte
          titel="Tickets je Woche"
          beschreibung="Neu gemeldet und abgeschlossen, letzte zwölf Wochen."
          legende={
            <Legende
              eintraege={[
                { label: 'Neu', farbe: KAT_1 },
                { label: 'Abgeschlossen', farbe: KAT_2 },
              ]}
            />
          }
          tabelle={{
            spalten: ['Woche ab', 'Neu', 'Abgeschlossen'],
            zeilen: [...daten.wochen].reverse().map((w) => [tagLang(w.woche), w.neu, w.geschlossen]),
          }}
        >
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daten.wochen} margin={{ top: 8, right: 8, bottom: 0, left: -16 }} barGap={2} maxBarSize={24}>
                <CartesianGrid {...RASTER} />
                <XAxis dataKey="woche" tickFormatter={tagKurz} {...ACHSE} />
                <YAxis allowDecimals={false} {...ACHSE} axisLine={false} />
                <Tooltip
                  cursor={{ fill: 'var(--viz-raster)', opacity: 0.4 }}
                  content={<VizTooltip labelFormat={(l) => `Woche ab ${tagLang(String(l))}`} />}
                />
                <Bar dataKey="neu" name="Neu" fill={KAT_1} radius={[4, 4, 0, 0]} isAnimationActive={false} />
                <Bar dataKey="geschlossen" name="Abgeschlossen" fill={KAT_2} radius={[4, 4, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartKarte>

        <ChartKarte
          titel="Offene Tickets und Blocker"
          beschreibung="Nur wirklich Offenes — was im Nachtest liegt, zählt hier nicht mehr."
          legende={
            <Legende
              eintraege={[
                { label: 'Offen', farbe: KAT_1, form: 'linie' },
                { label: 'Davon Blocker', farbe: KAT_2, form: 'linie' },
              ]}
            />
          }
          tabelle={{
            spalten: ['Tag', 'Offen', 'Blocker'],
            zeilen: [...ticketVerlauf].reverse().map((p) => [tagLang(p.tag), p.offen, p.blocker]),
          }}
        >
          {ticketVerlauf.length < 2 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              Der Verlauf entsteht, sobald mindestens zwei Tage festgehalten sind.
            </p>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={ticketVerlauf} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                  <CartesianGrid {...RASTER} />
                  <XAxis dataKey="tag" tickFormatter={tagKurz} minTickGap={24} {...ACHSE} />
                  <YAxis allowDecimals={false} {...ACHSE} axisLine={false} />
                  <Tooltip content={<VizTooltip labelFormat={(l) => tagLang(String(l))} />} />
                  <Line type="monotone" dataKey="offen" name="Offen" stroke={KAT_1} strokeWidth={2} dot={false} isAnimationActive={false} />
                  <Line type="monotone" dataKey="blocker" name="Davon Blocker" stroke={KAT_2} strokeWidth={2} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartKarte>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kachel
          titel="Bis zum Fix auf dem Testsystem"
          wert={tage(daten.durchlaufzeit.bisNachtestTage)}
          zusatz={`Median aus ${daten.durchlaufzeit.anzahlNachtest} Tickets`}
        />
        <Kachel
          titel="Bis erledigt"
          wert={tage(daten.durchlaufzeit.bisErledigtTage)}
          // Ohne diesen Zusatz liest man die Zahl falsch: Ein Ticket, das drei
          // Monate zurückgestellt war, hat niemanden drei Monate aufgehalten.
          zusatz={`Median aus ${daten.durchlaufzeit.anzahlErledigt} Tickets, ohne Backlog-Zeit`}
        />
        <Kachel
          titel="Nachtest zurückgewiesen"
          wert={prozent(daten.nachtest.quote)}
          zusatz={`${daten.nachtest.zurueck} von ${daten.nachtest.gesamt} Fixes`}
          ton={(daten.nachtest.quote ?? 0) > 25 ? 'warnung' : 'normal'}
        />
        <Kachel
          titel="Letzter CI-Lauf"
          wert={
            ci.length
              ? `${ci[ci.length - 1].bestanden} / ${ci[ci.length - 1].bestanden + ci[ci.length - 1].fehlgeschlagen}`
              : '—'
          }
          zusatz={ci.length ? `bestanden · ${tagLang(ci[ci.length - 1].am)}` : 'noch kein Lauf'}
        />
      </div>

      <ChartKarte
        titel="Fehlgeschlagene Tests je CI-Lauf"
        beschreibung="Die letzten 60 Läufe aus der Pipeline, Backend und Frontend getrennt."
        legende={
          <Legende
            eintraege={[
              { label: 'Backend', farbe: KAT_1, form: 'linie' },
              { label: 'Frontend', farbe: KAT_2, form: 'linie' },
            ]}
          />
        }
        tabelle={{
          spalten: ['Lauf', 'Art', 'Bestanden', 'Fehlgeschlagen', 'Coverage'],
          zeilen: [...ci]
            .reverse()
            .map((l) => [
              new Date(l.am).toLocaleString('de-DE'),
              l.art === 'ci_backend' ? 'Backend' : 'Frontend',
              l.bestanden,
              l.fehlgeschlagen,
              prozent(l.coverage),
            ]),
        }}
      >
        {ci.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">Noch keine CI-Läufe gemeldet.</p>
        ) : (
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={ciDaten} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                <CartesianGrid {...RASTER} />
                <XAxis dataKey="am" tickFormatter={tagKurz} minTickGap={32} {...ACHSE} />
                <YAxis allowDecimals={false} {...ACHSE} axisLine={false} />
                <Tooltip content={<VizTooltip labelFormat={(l) => new Date(String(l)).toLocaleString('de-DE')} />} />
                <Line
                  type="linear"
                  dataKey="ci_backend"
                  name="Backend"
                  stroke={KAT_1}
                  strokeWidth={2}
                  dot={{ r: 4, strokeWidth: 0, fill: KAT_1 }}
                  connectNulls
                  isAnimationActive={false}
                />
                <Line
                  type="linear"
                  dataKey="ci_frontend"
                  name="Frontend"
                  stroke={KAT_2}
                  strokeWidth={2}
                  dot={{ r: 4, strokeWidth: 0, fill: KAT_2 }}
                  connectNulls
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartKarte>
    </div>
  );
}
